import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MARKETS, marketFor } from "@/data/products";

/**
 * Which market the shopper sees prices + shipping copy for.
 *
 * Resolution order:
 *   1. Explicit override saved to localStorage (`tracksy-market`)
 *   2. Server-detected country code (via `/api/geo/country`, falls back to AU)
 *   3. Browser `Intl` timezone heuristic (America/* → US, else → AU) — only
 *      used as a synchronous first-render default while the fetch is in flight
 *
 * The context exposes everything downstream components need to render the
 * right currency, platform-availability guard, and shipping copy without each
 * component re-deriving its own.
 */

const STORAGE_KEY = "tracksy-market";

const guessFromTz = () => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (tz.startsWith("America/")) return "US";
    if (tz.startsWith("Australia/")) return "AU";
    if (tz.startsWith("Pacific/Auckland")) return "AU";  // NZ shoppers get AU pricing/shipping
    return "AU";
  } catch { return "AU"; }
};

const readStored = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v && MARKETS[v] ? v : null;
  } catch { return null; }
};

const CountryContext = createContext(null);

export function CountryProvider({ children }) {
  // Synchronous first-paint value — localStorage override > tz guess.
  const [code, setCodeState] = useState(() => readStored() || guessFromTz());
  const [hasOverride, setHasOverride] = useState(() => !!readStored());
  // Geo-detected region (ISO subdivision or full name) — used by components
  // that want to personalise copy per state (e.g. TheftUrgency). Not persisted,
  // not overridable by the toggle; stays null until the geo lookup resolves.
  const [region, setRegion] = useState(null);

  // Fetch the authoritative country from the backend geo endpoint; only applies
  // if the shopper hasn't manually picked a market yet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo/country`);
        if (!res.ok) return;
        const data = await res.json();
        const detected = (data?.country || "").toUpperCase();
        if (cancelled) return;
        if (!hasOverride && MARKETS[detected]) setCodeState(detected);
        if (data?.region) setRegion(String(data.region).toUpperCase());
      } catch { /* keep tz guess */ }
    })();
    return () => { cancelled = true; };
  }, [hasOverride]);

  const setCode = useCallback((next) => {
    if (!MARKETS[next]) return;
    try { localStorage.setItem(STORAGE_KEY, next); } catch {}
    setHasOverride(true);
    setCodeState(next);
  }, []);

  const market = useMemo(() => marketFor(code), [code]);

  const value = useMemo(() => ({
    code: market.code,
    market,
    currency: market.currency,
    symbol: market.symbol,
    flag: market.flag,
    supportedPlatforms: market.supportedPlatforms,
    isUS: market.code === "US",
    isAU: market.code === "AU",
    isGB: market.code === "GB",
    region,
    setCode,
  }), [market, region, setCode]);

  return <CountryContext.Provider value={value}>{children}</CountryContext.Provider>;
}

export const useCountry = () => {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error("useCountry must be used within CountryProvider");
  return ctx;
};
